import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuComponentBase } from '../../../../../shared/base-classes/menu-component-base';
import { finalize, takeUntil } from 'rxjs';
import { PaginationComponent } from '../../../../../shared/components/pagination/pagination.component';
import { PageSizeComponent } from '../../../../../shared/components/page-size/page-size.component';
import { FilterComponent } from '../../../../../shared/components/filter/filter.component';
import { SvgIconComponent } from '../../../../../shared/components/svg-icon/svg-icon.component';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LeadStatusLog } from '../../models/lead-status';
import { LeadStatusLogsDashboardComponent } from '../lead-status-logs-dashboard/lead-status-logs-dashboard.component';
import { ManageConnectionService } from '../../../../manage-connection-group/services/manage-connection.service';
import { LeadStatusValues } from '../../../../leads/models/lead-status';
import {
  TableColumn,
  TableComponent,
} from '../../../../../shared/components/table/table.component';
import { LeadStatusService } from '../../services/lead-status.service';
import { RecordDetailsComponent } from '../../../../../shared/components/record-details/record-details.component';

@Component({
  selector: 'app-lead-status-logs',
  standalone: true,
  imports: [
    CommonModule,
    PageSizeComponent,
    PaginationComponent,
    FilterComponent,
    SvgIconComponent,
    FormsModule,
    LeadStatusLogsDashboardComponent,
    TableComponent,
    RecordDetailsComponent,
  ],
  templateUrl: './lead-status-logs.component.html',
  styleUrl: './lead-status-logs.component.scss',
})
export class LeadStatusLogsComponent
  extends MenuComponentBase
  implements OnInit
{
  LeadStatusService = inject(LeadStatusService);
  manageConnectionService = inject(ManageConnectionService);
  router = inject(Router);

  currentPage = 1;
  pageSize = 50;
  loading = true;
  LeadStatusLog: LeadStatusLog[] = [];
  totalLeadStatusLogs = 0;
  tableColumns: TableColumn[] = [
    { header: 'Business Name', width: '18%' },
    { header: 'Connection', width: '18%' },
    { header: 'Updated By', width: '18%' },
    { header: 'Lead Status', width: '18%' },
    { header: 'Updated On', width: '18%' },
    { header: '', width: '10%' },
  ];
  showRecordDetails = false;
  uniqueId!: string;
  searchQuery = '';
  filterByConnection = 'all';
  filterByLeadStatus = '';
  filterByConnectionOptions = [
    {
      option: 'All',
      value: 'all',
    },
  ];
  allConnections: { id: string; name: string }[] = [];

  filterByLeadStatusOptions = [
    {
      option: 'All',
      value: '',
    },
    {
      option: 'Not set',
      value: LeadStatusValues['not_defined'],
    },
    {
      option: 'Under review',
      value: LeadStatusValues['under_review'],
    },
    {
      option: 'Further information required',
      value: LeadStatusValues['further_information_required'],
    },
    {
      option: 'Prospective client',
      value: LeadStatusValues['prospective_client'],
    },
    {
      option: 'On hold',
      value: LeadStatusValues['on_hold'],
    },
    {
      option: 'Declined',
      value: LeadStatusValues['declined'],
    },
    {
      option: 'Not currently pursuing',
      value: LeadStatusValues['not_currently_pursuing'],
    },
    {
      option: 'Active client',
      value: LeadStatusValues['active_client'],
    },
    {
      option: 'Request lead contact',
      value: LeadStatusValues['request_lead_contact'],
    },
    {
      option: 'Active Funded',
      value: LeadStatusValues['active_funded'],
    },
    {
      option: 'Active Funded Grant',
      value: LeadStatusValues['active_funded_grant'],
    },
    {
      option: 'Active Technical Assistance',
      value: LeadStatusValues['active_technical_assistance'],
    },
  ];

  leadStatusValueToClassMap: Record<string, string> = {
    [LeadStatusValues['not_defined']]: 'status-not-defined',
    [LeadStatusValues['under_review']]: 'status-under-review',
    [LeadStatusValues['further_information_required']]: 'status-further-info',
    [LeadStatusValues['prospective_client']]: 'status-prospective',
    [LeadStatusValues['on_hold']]: 'status-on-hold',
    [LeadStatusValues['declined']]: 'status-declined',
    [LeadStatusValues['not_currently_pursuing']]: 'status-not-pursuing',
    [LeadStatusValues['active_client']]: 'status-active-client',
    [LeadStatusValues['request_lead_contact']]: 'status-request-contact',
  };

  override ngOnInit(): void {
    super.ngOnInit();
    this.manageConnectionService
      .getConnectionsFromDB()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (connections) => {
          this.allConnections = connections.map((conn) => ({
            id: conn.connection_id,
            name: conn.connection_display_name,
          }));

          this.filterByConnectionOptions = [
            { option: 'All', value: 'all' },
            ...this.allConnections.map((conn) => ({
              option: conn.name,
              value: conn.id,
            })),
          ];
        },
        error: () => {
          this.filterByConnectionOptions = [{ option: 'All', value: 'all' }];
        },
      });

    this.loadLeadStatusLogs(this.currentPage, this.pageSize);
  }

  loadLeadStatusLogs(pageNumber: number, pageSize: number): void {
    this.loading = true;
    const connectionId =
      this.filterByConnection !== 'all'
        ? Number(this.filterByConnection)
        : undefined;
    const searchParam = this.searchQuery ? this.searchQuery : undefined;
    const leadStatus =
      this.filterByLeadStatus !== ''
        ? Number(this.filterByLeadStatus)
        : undefined;

    this.LeadStatusService.getLeadStatusLogs(
      pageNumber,
      pageSize,
      connectionId,
      leadStatus,
      searchParam,
    )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response) => {
          this.LeadStatusLog = response.results;
          this.totalLeadStatusLogs = response.count;
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  viewRecordDetails(uniqueId: string): void {
    this.uniqueId = uniqueId;
    this.showRecordDetails = true;
  }

  onFilterByConnectionChange(value: string): void {
    this.filterByConnection = value;
    this.currentPage = 1;
    this.loadLeadStatusLogs(this.currentPage, this.pageSize);

    const filterParam = value === 'all' ? '' : value;
    this.router.navigate([], {
      queryParams: { connection: filterParam },
      queryParamsHandling: 'merge',
    });
  }

  onFilterByLeadStatusChange(value: string): void {
    this.filterByLeadStatus = value;
    this.currentPage = 1;
    this.loadLeadStatusLogs(this.currentPage, this.pageSize);

    const filterParam = value === 'all' ? '' : value;
    this.router.navigate([], {
      queryParams: { leadStatus: filterParam },
      queryParamsHandling: 'merge',
    });
  }

  onDashboardStatusClick(statusLabel: string): void {
    const statusKey = statusLabel
      .toLowerCase()
      .replace(/\s+/g, '_') as keyof typeof LeadStatusValues;

    const statusValue = LeadStatusValues[statusKey];

    if (!statusValue) return;
    this.filterByLeadStatus = statusValue;
    this.currentPage = 1;
    this.loadLeadStatusLogs(this.currentPage, this.pageSize);
    this.router.navigate([], {
      queryParams: { leadStatus: statusValue },
      queryParamsHandling: 'merge',
    });
  }

  onSearchQueryChange(query: string) {
    this.searchQuery = query;
    this.currentPage = 1;
    this.loadLeadStatusLogs(this.currentPage, this.pageSize);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadLeadStatusLogs(this.currentPage, this.pageSize);
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadLeadStatusLogs(this.currentPage, this.pageSize);
    }
  }

  getLeadStatusLabel(leadStatus: number | undefined): string {
    if (leadStatus === undefined || leadStatus === null) return 'Not Set';

    const statusEntry = Object.entries(LeadStatusValues).find(
      ([, value]) => value === leadStatus.toString(),
    );

    if (!statusEntry) return 'Not Set';

    // Convert snake_case to Title Case
    return statusEntry[0]
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getLeadStatusClass(status: number | string | undefined): string {
    if (status === undefined || status === null) {
      return 'status-not-defined';
    }

    return (
      this.leadStatusValueToClassMap[String(status)] ?? 'status-not-defined'
    );
  }
}
